import { useAddFinancialDocumentsToImporterMutation } from "@api/financialDocument.api";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { FinancialDocumentSelect } from "@components/documents/FinancialDocumentSelect/FinancialDocumentSelect";
import { DocumentSelectVariant } from "@components/documents/FinancialDocumentSelect/financialDocumentSelectViewConfig";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralizeWithCount } from "@common/helpers/string";
import { useNotifyUnfinishedImport } from "@components/importer/useNotifyUnfinishedImport";
import { useCallback, useContext, useMemo, useState } from "react";
import { AccrualImporterContext } from "./accrualImporter.context";
import { AccrualImporterPickExistingDocumentCta } from "./AccrualImporterPickExistingDocumentCta";
import { getAccrualImporterDocumentEligibility } from "./accrualImporterDocumentEligibility";

export const AccrualImporterPickExistingDocument: React.FC = () => {
    const { mode, importerQueue } = useContext(AccrualImporterContext);
    const [documents, setDocuments] = useState<FinancialDocumentListItem[]>([]);
    const toaster = useToaster();
    const notifyUnfinishedImport = useNotifyUnfinishedImport(mode);

    const documentEligibilityById = useMemo(
        () =>
            new Map(
                documents.map((financialDocument) => [
                    financialDocument.id,
                    getAccrualImporterDocumentEligibility({
                        financialDocument,
                        importerQueue,
                    }),
                ]),
            ),
        [documents, importerQueue],
    );

    const nonSelectableDocumentIds = useMemo(
        () =>
            documents
                .filter(
                    (financialDocument) =>
                        documentEligibilityById.get(financialDocument.id)
                            ?.isDisabled,
                )
                .map((financialDocument) => financialDocument.id),
        [documents, documentEligibilityById],
    );

    const addDocuments = useAddFinancialDocumentsToImporterMutation({
        importerQueue,
        onSettled: ({ updatedDocuments, failedCount }) => {
            if (failedCount > 0) {
                toaster.error(
                    `Failed to add ${pluralizeWithCount("document", failedCount)} to the importer`,
                );
            }
            notifyUnfinishedImport(updatedDocuments);
        },
    });

    const attachDocuments = useCallback(
        (documentIds: number[]) => {
            const selectedDocuments = documents.filter((financialDocument) =>
                documentIds.includes(financialDocument.id),
            );
            if (selectedDocuments.length === 0) {
                return;
            }
            addDocuments.mutate(selectedDocuments);
        },
        [documents, addDocuments],
    );

    const getRowSelectionDisabledTooltip = useCallback(
        (financialDocument: FinancialDocumentListItem) =>
            documentEligibilityById.get(financialDocument.id)?.disabledTooltip,
        [documentEligibilityById],
    );

    return (
        <FinancialDocumentSelect
            variant={DocumentSelectVariant.IMPORTER}
            cta={(financialDocument) => (
                <AccrualImporterPickExistingDocumentCta
                    financialDocument={financialDocument}
                />
            )}
            attachedDocumentIds={nonSelectableDocumentIds}
            attachDocuments={attachDocuments}
            getRowSelectionDisabledTooltip={getRowSelectionDisabledTooltip}
            onDocumentsChanged={setDocuments}
        />
    );
};
